import React, { memo } from 'react';
import useWindowSize from 'app/hooks/useWindowSize';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { useSelector } from 'react-redux';
import ModalContainer from '../ModalContainer';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import { Col, Row, Form, Divider } from 'antd';
import { formatNumber } from 'utils/helper';
import StyledButton from '../StyledButton';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { MAX_AMOUNT } from 'utils/constants';
import TicketDateSelectable from './TicketDateSelectable';
import moment from 'moment';
import { InputNumberContainer } from 'app/components/InputNumberContainer/Loadable';

export const AddonsTicketModal = memo(
  ({
    open,
    onClose,
    listAddons,
    handleChangeAddonsAmount,
    handleAdd,
    isLoading,
    handleChangeDate,
    disableSubmit,
    dateAddons,
  }: {
    open: boolean;
    onClose: () => void;
    listAddons: any[];
    handleChangeAddonsAmount: (
      value: any,
      id: string,
      validByJumpValue?: boolean,
      event?: 'change' | 'blur',
    ) => void;
    handleAdd: () => void;
    isLoading: boolean;
    handleChangeDate: (value: any) => Promise<void>;
    disableSubmit: boolean;
    dateAddons: string;
  }) => {
    const [form] = Form.useForm();
    const { device } = useWindowSize();
    const isMobile = device === 'mobile';
    const translations = useSelector(selectTranslations);
    return (
      <ModalContainer
        isShow={open}
        maskClosable={false}
        onCloseDialog={onClose}
        CloseIcon={<CloseIcon width={12} height={12} />}
        modalPadding="0"
        styleSlots={{
          iconWrapperStyle: {
            width: 12,
            height: 12,
          },
          modalContentStyle: {
            height: 'auto',
            width: 'auto',
            borderRadius: '16px',
            overflow: 'unset',
            maxWidth: '516px',
          },
          modalStyle: {
            width: 'auto',
          },
          bodyStyle: {
            width: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
        }}
      >
        <Form
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          form={form}
        >
          <LoadingContainer loading={isLoading}>
            <Row gutter={[4, 4]} style={{ padding: '35px 35px' }}>
              <Col
                span={24}
                style={{ fontWeight: '600', fontSize: '20px' }}
                className="mb-2"
              >
                {translations?.['b2b.text.notify'] ?? ''}
              </Col>
              <Col span={24}>{listAddons[0]?.b2bTicketAddonNote1}</Col>
              <Col span={24}>{listAddons[0]?.b2bTicketAddonNote2}</Col>
              <Col span={24} className="mt-2">
                <Row>
                  <Col
                    span={isMobile ? 24 : 12}
                    className="d-flex align-items-center"
                  >
                    {translations?.['b2b.txt.usageDate'] ?? ''}
                  </Col>
                  <Col
                    span={isMobile ? 24 : 12}
                    className={'addons-date-picker' + (isMobile ? ' mt-2' : '')}
                    style={{ position: 'relative', zIndex: 1000 }}
                  >
                    <TicketDateSelectable
                      onChange={handleChangeDate}
                      defaultValue={moment(dateAddons)}
                      value={moment(dateAddons)}
                      disabled={isLoading}
                      getPopupContainer={(trigger: any) => trigger.parentNode}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={24} className="mt-2">
                {listAddons.length > 0 &&
                  listAddons.map((item: any, index: number) => {
                    return (
                      <Row className={index === 0 ? 'mb-2' : ''}>
                        <Col
                          className="d-flex flex-column pe-3 mb-1 mb-sm-0"
                          span={isMobile ? 24 : 12}
                        >
                          <div>{item?.name}</div>
                          <div>
                            {item?.price
                              ? ` (${formatNumber(item.price)} ${
                                  translations?.['b2b.text.moneyUnit'] ?? ''
                                })`
                              : ''}
                          </div>
                        </Col>
                        <Col
                          span={isMobile ? 12 : 5}
                          className="d-flex align-items-center"
                        >
                          <div
                            className="form-inputNumber"
                            style={{ width: 'fit-content', minWidth: '65px' }}
                          >
                            <InputNumberContainer
                              value={+item?.amount || 0}
                              onChange={(
                                value,
                                validByJumpValue?: boolean,
                                event?: 'change' | 'blur',
                              ) =>
                                handleChangeAddonsAmount(
                                  value,
                                  item?.id,
                                  validByJumpValue,
                                  event,
                                )
                              }
                              step={item?.jumpValueB2b}
                              min={0}
                              max={
                                Number(item?.inventory || 0) >= MAX_AMOUNT
                                  ? MAX_AMOUNT
                                  : item?.inventory
                              }
                              isApiCall
                              disabled={isLoading}
                            />
                          </div>
                        </Col>
                        <Col
                          span={isMobile ? 12 : 7}
                          className="d-flex align-items-center justify-content-end"
                        >
                          {item?.displayPrice
                            ? formatNumber(item?.displayPrice)
                            : 0}
                          đ
                        </Col>
                        <Col
                          span={24}
                          className={item?.error ? 'mt-2 mb-2' : ''}
                          style={{
                            color: '#E55015',
                            fontStyle: 'italic',
                          }}
                        >
                          {item?.error}
                        </Col>
                      </Row>
                    );
                  })}
              </Col>
              <Divider style={{ margin: '24px 0 14px 0' }}></Divider>
              <Col
                span={24}
                className="d-flex flex-column flex-sm-row align-items-center justify-content-between"
              >
                <div className="d-flex flex-sm-column flex-row justify-content-between w-100 w-sm-auto mb-2 mb-sm-0">
                  <div style={{ color: '#808080' }}>
                    {translations?.['b2b.txt.total'] ?? ''}
                  </div>
                  <div
                    style={{
                      color: '#E92127',
                      fontWeight: '600',
                      fontSize: '18px',
                    }}
                  >
                    {formatNumber(
                      listAddons.reduce((sum: number, item: any) => {
                        return (
                          sum + (+item?.displayPrice ? +item?.displayPrice : 0)
                        );
                      }, 0),
                    )}
                    đ
                  </div>
                </div>
                <div className="d-flex w-100 w-sm-auto justify-content-sm-end">
                  <StyledButton
                    textColor="#FAB317"
                    border="solid 1px #FAB317"
                    bgColor="#fff"
                    height="40px"
                    width={isMobile ? '50%' : 'auto'}
                    onClick={onClose}
                  >
                    {translations?.['b2b.txt.skip'] ?? ''}
                  </StyledButton>
                  <StyledButton
                    textColor="#fff"
                    height="40px"
                    width={isMobile ? '50%' : 'auto'}
                    style={{ marginLeft: 8 }}
                    onClick={handleAdd}
                    disabled={
                      listAddons.some(
                        item => item?.error && +item?.amount > 0,
                      ) || disableSubmit
                    }
                  >
                    {translations?.['b2b.txt.add'] ?? ''}
                  </StyledButton>
                </div>
              </Col>
            </Row>
          </LoadingContainer>
        </Form>
      </ModalContainer>
    );
  },
);
